import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ScopeType, UserScope } from '@shared/scope';

interface ScopeContextType {
  scope: ScopeType;
  teamId: string | null;
  setScope: (scope: ScopeType, teamId?: string | null) => void;
  isLoading: boolean;
}

const ScopeContext = createContext<ScopeContextType | undefined>(undefined);

const SCOPE_STORAGE_KEY = 'joya-crm-scope';

interface StoredScope {
  scope: ScopeType;
  teamId?: string | null;
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeState] = useState<ScopeType>('my');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load scope from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SCOPE_STORAGE_KEY);
      if (stored) {
        const parsed: StoredScope = JSON.parse(stored);
        setScopeState(parsed.scope);
        setTeamId(parsed.teamId || null);
      }
    } catch (error) {
      console.error('Failed to load scope from storage:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setScope = (newScope: ScopeType, newTeamId?: string | null) => {
    setScopeState(newScope);
    setTeamId(newTeamId || null);
    
    // Persist to localStorage
    try {
      const toStore: StoredScope = {
        scope: newScope,
        teamId: newTeamId || null,
      };
      localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(toStore));
    } catch (error) {
      console.error('Failed to save scope to storage:', error);
    }
  };

  return (
    <ScopeContext.Provider value={{ scope, teamId, setScope, isLoading }}>
      {children}
    </ScopeContext.Provider>
  );
}

export function useScope() {
  const context = useContext(ScopeContext);
  if (context === undefined) {
    throw new Error('useScope must be used within a ScopeProvider');
  }
  return context;
}

